/**
 * Expenses Page (지출기록 / Dépenses)
 * 
 * Manage expense records from Crédit Agricole bank statements.
 * Access: configured expenses operator only
 * 
 * Features:
 * - Year/Month scroll picker
 * - Upload relevé de compte PDFs (multiple)
 * - Auto-parsed transaction table
 * - Receipt attachment per transaction (single or batch)
 * - Download: original relevé, all receipts as ZIP
 */
import React, { useState, useEffect, useLayoutEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { UserContext } from '../contexts/UserContext';
import { canDownloadFacture, hasExpensesAccess } from '../utils/permissionConfig';
import { apiCall } from '../utils/auth';
import * as expensesApi from '../services/expensesApi';
import { exportExpenses } from '../services/expensesExport';
import { ACCOUNTING_DOCUMENT_CONFIG } from '../services/accountingDocuments';
import {
    getAccountingInvoicePeriod,
    issueAccountingInvoices,
    reopenAccountingInvoicePeriod
} from '../services/accountingInvoiceReferenceApi';
import ExpensePaymentMatchModal from '../components/ExpensePaymentMatchModal';
import paymentMatchStyles from '../components/ExpensePaymentMatchModal.module.css';
import { MONTHS_FR, MONTHS_FR_SHORT } from '../constants/expenseMonths';
import pageStyles from './ExpensesPage.module.css';

const INVOICE_TYPE_OPTIONS = Object.entries(ACCOUNTING_DOCUMENT_CONFIG.types)
    .map(([type, config]) => [type, config.label]);
const INVOICE_EXPORT_SCOPE_OPTIONS = Object.entries(ACCOUNTING_DOCUMENT_CONFIG.exportScopes)
    .map(([scope, config]) => [scope, config.label]);

export default function ExpensesPage() {
    const { userInfo } = useContext(UserContext);
    const hasAccess = hasExpensesAccess(userInfo);
    const canExportFacture = canDownloadFacture(userInfo);
    const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
    const [expenses, setExpenses] = useState([]);
    const [monthStatus, setMonthStatus] = useState({});
    const [statements, setStatements] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const receiptInputRef = useRef(null);
    const pageRef = useRef(null);
    const tableViewportRef = useRef(null);
    const paymentMatchPositionRef = useRef(null);
    const pendingPaymentMatchRestoreRef = useRef(false);

    // Batch receipt selection mode
    const [batchMode, setBatchMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [creditGroupMode, setCreditGroupMode] = useState(false);
    const [selectedCreditIds, setSelectedCreditIds] = useState(new Set());

    // Receipt preview modal
    const [previewExpense, setPreviewExpense] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    // Memo / note expansion
    const [expandedExpenseId, setExpandedExpenseId] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [savingFinalCheckIds, setSavingFinalCheckIds] = useState(new Set());
    const [paymentMatchExpenses, setPaymentMatchExpenses] = useState([]);

    // Export modal
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportOptions, setExportOptions] = useState({
        summary: true,
        receipts: true,
        statements: true,
        invoices: true,
        invoiceList: true,
        invoiceFiles: true,
        invoiceScope: 'all',
        invoiceTypes: { tattoo: true, deposit: true, commission: true }
    });
    const [exportData, setExportData] = useState({ invoices: [], reconciliations: [], summary: null });
    const [exportDataLoading, setExportDataLoading] = useState(false);
    const [exportDataError, setExportDataError] = useState('');
    const [exportProgress, setExportProgress] = useState(null); // null = idle, 0-100 = in progress
    const [invoicePeriod, setInvoicePeriod] = useState({ status: 'draft', current_version: 0 });
    const [invoicePeriodUpdating, setInvoicePeriodUpdating] = useState(false);
    const currentYear = useMemo(() => new Date().getFullYear(), []);

    // Statement preview modal
    const [previewStatement, setPreviewStatement] = useState(null);
    const [previewStatementUrl, setPreviewStatementUrl] = useState(null);

    // Load receipt blob for preview
    useEffect(() => {
        if (!previewExpense?.receipt_url) {
            setPreviewUrl(null);
            return;
        }
        let revoked = false;
        const loadPreview = async () => {
            try {
                const res = await apiCall(previewExpense.receipt_url);
                if (!res) throw new Error('Unauthorized');
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                if (!revoked) {
                    setPreviewUrl(URL.createObjectURL(blob));
                }
            } catch (err) {
                console.error('Receipt preview error:', err);
                // Fallback: open in new tab
                if (!revoked) setPreviewUrl(null);
            }
        };
        loadPreview();
        return () => {
            revoked = true;
            setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
        };
    }, [previewExpense]);

    // PWA theme color
    useEffect(() => {
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute('content', '#fffdf8');
    }, []);

    const loadExpenses = useCallback(async ({ background = false } = {}) => {
        if (!background) setIsLoading(true);
        try {
            const data = await expensesApi.getExpenses(selectedYear, selectedMonth);
            console.log('[Expenses] GET response:', data);
            // Handle both {success: true, expenses: [...]} and {expenses: [...]} formats
            const expensesList = data.expenses || [];
            const statementsList = data.statements || [];
            setExpenses(expensesList);
            setStatements(statementsList);
        } catch (err) {
            console.error('Failed to load expenses:', err);
            if (background) throw err;
            setExpenses([]);
            setStatements([]);
        } finally {
            if (!background) setIsLoading(false);
        }
    }, [selectedYear, selectedMonth]);

    const openExportModal = useCallback(async () => {
        setShowExportModal(true);
        setExportDataError('');
        if (!canExportFacture) return;
        setExportDataLoading(true);
        try {
            const [data, period] = await Promise.all([
                expensesApi.getExpensesExportData(selectedYear, selectedMonth),
                getAccountingInvoicePeriod(selectedYear, selectedMonth)
            ]);
            setExportData({
                invoices: data.invoices || [],
                reconciliations: data.reconciliations || [],
                summary: data.summary || null
            });
            setInvoicePeriod(period || { status: 'draft', current_version: 0 });
        } catch (error) {
            console.error('Failed to load expense export data:', error);
            setExportData({ invoices: [], reconciliations: [], summary: null });
            setExportDataError('Impossible de charger les factures associées.');
        } finally {
            setExportDataLoading(false);
        }
    }, [canExportFacture, selectedMonth, selectedYear]);

    const reopenInvoicePeriod = useCallback(async () => {
        if (invoicePeriodUpdating) return;
        setInvoicePeriodUpdating(true);
        setExportDataError('');
        try {
            const period = await reopenAccountingInvoicePeriod(selectedYear, selectedMonth);
            setInvoicePeriod(period);
        } catch (error) {
            console.error('Failed to reopen accounting invoice period:', error);
            setExportDataError(error.message || 'Impossible de réouvrir la période.');
        } finally {
            setInvoicePeriodUpdating(false);
        }
    }, [invoicePeriodUpdating, selectedMonth, selectedYear]);

    useLayoutEffect(() => {
        if (!pendingPaymentMatchRestoreRef.current) return;
        pendingPaymentMatchRestoreRef.current = false;

        const position = paymentMatchPositionRef.current;
        if (!position) return;

        if (tableViewportRef.current) {
            tableViewportRef.current.scrollLeft = position.tableScrollLeft;
        }

        const anchorRow = pageRef.current?.querySelector(`[data-expense-id="${position.expenseId}"]`);
        if (anchorRow && position.rowTop !== null) {
            const topDifference = anchorRow.getBoundingClientRect().top - position.rowTop;
            if (Math.abs(topDifference) > 0.5) {
                window.scrollBy({ top: topDifference, left: 0, behavior: 'auto' });
            }
        } else {
            window.scrollTo({ top: position.pageScrollY, left: 0, behavior: 'auto' });
        }
    }, [expenses]);

    const openPaymentMatchModal = useCallback((event, selectedExpenses) => {
        const credits = Array.isArray(selectedExpenses) ? selectedExpenses : [selectedExpenses];
        const anchorExpense = credits[0];
        if (!anchorExpense) return;
        const anchorRow = event?.currentTarget?.closest('tr') ||
            pageRef.current?.querySelector(`[data-expense-id="${anchorExpense.id}"]`);
        paymentMatchPositionRef.current = {
            expenseId: anchorExpense.id,
            rowTop: anchorRow ? anchorRow.getBoundingClientRect().top : null,
            pageScrollY: window.scrollY,
            tableScrollLeft: tableViewportRef.current?.scrollLeft || 0
        };
        setPaymentMatchExpenses(credits);
    }, []);

    const refreshAfterPaymentMatch = useCallback(async () => {
        pendingPaymentMatchRestoreRef.current = true;
        try {
            await loadExpenses({ background: true });
        } catch (error) {
            pendingPaymentMatchRestoreRef.current = false;
            throw error;
        }
    }, [loadExpenses]);

    const toggleCreditGroupSelection = useCallback(expense => {
        if (!(parseAmount(expense.credit) > 0) || expense.payment_group_id) return;
        setSelectedCreditIds(current => {
            const next = new Set(current);
            next.has(expense.id) ? next.delete(expense.id) : next.add(expense.id);
            return next;
        });
    }, []);

    const cancelCreditGroupMode = useCallback(() => {
        setCreditGroupMode(false);
        setSelectedCreditIds(new Set());
    }, []);

    const openSelectedCreditGroup = useCallback(event => {
        const selectedExpenses = expenses.filter(item => selectedCreditIds.has(item.id));
        if (selectedExpenses.length < 2) return;
        openPaymentMatchModal(event, selectedExpenses);
        cancelCreditGroupMode();
    }, [cancelCreditGroupMode, expenses, openPaymentMatchModal, selectedCreditIds]);

    // Load data when year/month changes
    useEffect(() => {
        loadExpenses();
    }, [loadExpenses]);

    // Month picker flags follow every change to the shown month and its period.
    useEffect(() => {
        let cancelled = false;
        expensesApi.getMonthStatus(selectedYear)
            .then((data) => {
                if (cancelled) return;
                setMonthStatus(Object.fromEntries((data.months || []).map(status => [status.month, status])));
            })
            .catch((err) => console.error('Failed to load month status:', err));
        return () => { cancelled = true; };
    }, [selectedYear, expenses, invoicePeriod.status]);

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
    };

    // Format amount
    const formatAmount = (amount) => {
        if (amount === null || amount === undefined) return '';
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;
        if (isNaN(num)) return '';
        return num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const parseAmount = (val) => {
        if (val === null || val === undefined) return 0;
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? 0 : num;
    };

    // === PDF Upload ===
    const handleFileUpload = async (files) => {
        if (!files || files.length === 0) return;
        const pdfFiles = Array.from(files).filter(f => f.type === 'application/pdf');
        if (pdfFiles.length === 0) {
            alert('Veuillez sélectionner des fichiers PDF.');
            return;
        }
        setIsUploading(true);
        try {
            const result = await expensesApi.uploadStatement(pdfFiles, selectedYear, selectedMonth);
            console.log('[Expenses] Upload response:', result);
            // Use upload response data directly if available
            if (result.expenses && result.expenses.length > 0) {
                setExpenses(prev => [...prev, ...result.expenses]);
                if (result.statements) {
                    setStatements(prev => [...prev, ...result.statements]);
                }
            }
            // Also re-fetch to get full data with summary
            await loadExpenses();
            const unbalanced = (result.statements || []).filter(s => s.totals_check?.matches === false);
            if (unbalanced.length > 0) {
                alert(
                    'Attention : les totaux lus ne correspondent pas au relevé. Des opérations ont peut-être été mal lues :\n'
                    + unbalanced.map(s => `- ${s.original_filename}`).join('\n')
                );
            }
        } catch (err) {
            console.error('Upload error:', err);
            alert(err.message || 'Erreur lors de l\'upload.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // === Image Compression ===
    const compressImage = (file, maxSize = 1280, quality = 0.8) => {
        return new Promise((resolve) => {
            if (!file.type.startsWith('image/')) return resolve(file);
            const img = new Image();
            img.onload = () => {
                let { width, height } = img;
                if (width <= maxSize && height <= maxSize && file.size < 500 * 1024) {
                    return resolve(file);
                }
                if (width > height) {
                    if (width > maxSize) { height = Math.round(height * maxSize / width); width = maxSize; }
                } else {
                    if (height > maxSize) { width = Math.round(width * maxSize / height); height = maxSize; }
                }
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => {
                    const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
                    console.log(`[Receipt] Compressed ${(file.size / 1024).toFixed(0)}KB → ${(compressed.size / 1024).toFixed(0)}KB`);
                    resolve(compressed);
                }, 'image/jpeg', quality);
            };
            img.onerror = () => resolve(file);
            img.src = URL.createObjectURL(file);
        });
    };
    // === Single Receipt Upload ===
    const handleReceiptUpload = async (expenseId, file) => {
        if (!file) return;
        try {
            const compressed = await compressImage(file);
            const result = await expensesApi.uploadReceipt(compressed, [expenseId]);
            if (result.success) {
                setExpenses(prev => prev.map(e =>
                    result.updated_ids.includes(e.id)
                        ? { ...e, receipt_url: result.receipt_url, has_receipt: true }
                        : e
                ));
            }
        } catch (err) {
            console.error('Receipt upload error:', err);
            alert('Erreur lors de l\'upload du justificatif.');
        }
    };

    // === Batch Receipt Upload ===
    const handleBatchReceiptUpload = async (file) => {
        if (!file || selectedIds.size === 0) return;
        try {
            const compressed = await compressImage(file);
            const result = await expensesApi.uploadReceipt(compressed, Array.from(selectedIds));
            if (result.success) {
                setExpenses(prev => prev.map(e =>
                    result.updated_ids.includes(e.id)
                        ? { ...e, receipt_url: result.receipt_url, has_receipt: true }
                        : e
                ));
                setBatchMode(false);
                setSelectedIds(new Set());
            }
        } catch (err) {
            console.error('Batch receipt error:', err);
            alert('Erreur lors de l\'upload du justificatif.');
        } finally {
            if (receiptInputRef.current) receiptInputRef.current.value = '';
        }
    };

    // === Receipt Delete ===
    const handleReceiptDelete = async (expenseId) => {
        try {
            const result = await expensesApi.deleteReceipt(expenseId);
            if (result.success) {
                setExpenses(prev => prev.map(e =>
                    e.id === expenseId ? { ...e, receipt_url: null, has_receipt: false } : e
                ));
            }
        } catch (err) {
            console.error('Receipt delete error:', err);
        }
    };

    // === Link Receipt (group/share existing receipt) ===
    const handleLinkReceipt = async () => {
        const sourceExp = expenses.find(e => selectedIds.has(e.id) && (e.has_receipt || e.receipt_url));
        if (!sourceExp) return;
        const targetIds = Array.from(selectedIds).filter(id => id !== sourceExp.id);
        if (targetIds.length === 0) return;
        try {
            const result = await expensesApi.linkReceipt(sourceExp.id, targetIds);
            if (result.success) {
                setExpenses(prev => prev.map(e =>
                    selectedIds.has(e.id)
                        ? { ...e, receipt_url: result.receipt_url || sourceExp.receipt_url, has_receipt: true, receipt_filename: result.receipt_filename || sourceExp.receipt_filename }
                        : e
                ));
                setSelectedIds(new Set());
            }
        } catch (err) {
            console.error('Link receipt error:', err);
            alert('Erreur lors du groupement des justificatifs.');
        }
    };

    // === Unlink Receipt (remove from group) ===
    const handleUnlinkReceipt = async () => {
        const idsToUnlink = Array.from(selectedIds).filter(id => {
            const exp = expenses.find(e => e.id === id);
            return exp && exp.receipt_url && receiptGroups.idToGroup[id];
        });
        if (idsToUnlink.length === 0) return;
        try {
            for (const id of idsToUnlink) {
                await expensesApi.deleteReceipt(id);
            }
            setExpenses(prev => prev.map(e =>
                idsToUnlink.includes(e.id)
                    ? { ...e, receipt_url: null, has_receipt: false, receipt_filename: null }
                    : e
            ));
            setSelectedIds(new Set());
        } catch (err) {
            console.error('Unlink receipt error:', err);
            alert('Erreur lors de la dissociation.');
        }
    };

    // === Mark N/A (no receipt needed) ===
    const handleMarkNA = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        try {
            const result = await expensesApi.markCPA(ids);
            if (result.success) {
                setExpenses(prev => prev.map(e =>
                    ids.includes(e.id) ? { ...e, cpa_exempt: true } : e
                ));
                setSelectedIds(new Set());
            }
        } catch (err) {
            console.error('Mark N/A error:', err);
            alert('Erreur lors du marquage N/A.');
        }
    };

    // === Unmark N/A ===
    const handleUnmarkNA = async () => {
        const ids = Array.from(selectedIds).filter(id => {
            const exp = expenses.find(e => e.id === id);
            return exp && exp.cpa_exempt;
        });
        if (ids.length === 0) return;
        try {
            const result = await expensesApi.unmarkCPA(ids);
            if (result.success) {
                setExpenses(prev => prev.map(e =>
                    ids.includes(e.id) ? { ...e, cpa_exempt: false } : e
                ));
                setSelectedIds(new Set());
            }
        } catch (err) {
            console.error('Unmark N/A error:', err);
            alert('Erreur lors du retrait N/A.');
        }
    };

    // === Save Note ===
    const handleSaveNote = async (expenseId) => {
        try {
            await expensesApi.updateNote(expenseId, noteText);
            setExpenses(prev => prev.map(e =>
                e.id === expenseId ? { ...e, note: noteText } : e
            ));
            setExpandedExpenseId(null);
        } catch (err) {
            console.error('Save note error:', err);
            alert('Erreur lors de la sauvegarde du mémo.');
        }
    };

    // === Final review check ===
    const handleFinalCheck = async (expense) => {
        if (savingFinalCheckIds.has(expense.id)) return;

        const nextChecked = !expense.final_checked;
        setSavingFinalCheckIds(prev => new Set(prev).add(expense.id));
        setExpenses(prev => prev.map(item =>
            item.id === expense.id
                ? {
                    ...item,
                    final_checked: nextChecked,
                    final_checked_at: nextChecked ? new Date().toISOString() : null
                }
                : item
        ));

        try {
            const result = await expensesApi.setFinalCheck(expense.id, nextChecked);
            if (!result.success || !result.expense) {
                throw new Error('Invalid final check response');
            }

            setExpenses(prev => prev.map(item =>
                item.id === expense.id ? { ...item, ...result.expense } : item
            ));
        } catch (err) {
            console.error('Final check error:', err);
            setExpenses(prev => prev.map(item =>
                item.id === expense.id
                    ? {
                        ...item,
                        final_checked: expense.final_checked,
                        final_checked_at: expense.final_checked_at || null
                    }
                    : item
            ));
            alert('Erreur lors de la mise à jour de la vérification.');
        } finally {
            setSavingFinalCheckIds(prev => {
                const next = new Set(prev);
                next.delete(expense.id);
                return next;
            });
        }
    };

    // === Drag & drop ===
    const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
    const handleDragLeave = () => setIsDragOver(false);
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFileUpload(e.dataTransfer.files);
    };

    // === Statement Delete ===
    const handleDeleteStatement = async (statementId, filename) => {
        if (!window.confirm(`Supprimer "${filename}" et toutes ses opérations ?`)) return;
        try {
            await expensesApi.deleteStatement(statementId);
            await loadExpenses();
        } catch (err) {
            console.error('Delete statement error:', err);
            alert('Erreur lors de la suppression.');
        }
    };

    // === Downloads ===
    const handleDownloadReleve = async (statement) => {
        setPreviewStatement(statement);
        setPreviewStatementUrl(null);
        try {
            const res = await apiCall(`/api/expenses/statement/${statement.id}/download`);
            if (!res) throw new Error('Unauthorized');
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            setPreviewStatementUrl(URL.createObjectURL(blob));
        } catch (err) {
            console.error('Statement preview error:', err);
            setPreviewStatementUrl('error');
        }
    };

    const handleDownloadAllReceipts = async () => {
        try {
            await expensesApi.downloadReceiptsZip(selectedYear, selectedMonth);
        } catch (err) {
            console.error('Receipts ZIP download error:', err);
            alert('Erreur lors du téléchargement des justificatifs.');
        }
    };

    // === Batch selection ===
    const toggleSelection = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const cancelBatchMode = () => {
        setBatchMode(false);
        setSelectedIds(new Set());
    };

    // === Computed ===
    const receiptCount = expenses.filter(e => e.has_receipt || e.receipt_url).length;
    const invoiceCounts = exportData.summary?.invoice_counts || { tattoo: 0, deposit: 0, commission: 0 };
    const selectedInvoiceCount = Object.entries(invoiceCounts).reduce(
        (total, [type, count]) => total + (exportOptions.invoiceTypes[type] !== false ? Number(count) || 0 : 0),
        0
    );
    const hasAnyExportOption = exportOptions.summary || exportOptions.receipts || exportOptions.statements ||
        (canExportFacture && exportOptions.invoices && selectedInvoiceCount > 0 &&
            (exportOptions.invoiceList || exportOptions.invoiceFiles));
    const verifiedCount = expenses.filter(e => e.final_checked).length;
    const selectedCreditTotal = expenses.reduce((sum, item) => (
        selectedCreditIds.has(item.id) ? sum + parseAmount(item.credit) : sum
    ), 0);
    const computedSummary = useMemo(() => {
        const totalDebit = expenses.reduce((sum, e) => sum + parseAmount(e.debit), 0);
        const totalCredit = expenses.reduce((sum, e) => sum + parseAmount(e.credit), 0);
        return { totalDebit, totalCredit, balance: totalCredit - totalDebit };
    }, [expenses]);

    // Compute receipt groups: { url -> { number, memberIds, receipt_url } }
    const receiptGroups = useMemo(() => {
        const urlToIds = {};
        expenses.forEach(e => {
            if (e.receipt_url) {
                if (!urlToIds[e.receipt_url]) urlToIds[e.receipt_url] = [];
                urlToIds[e.receipt_url].push(e.id);
            }
        });
        const groups = {}; // url -> { number, memberIds }
        const idToGroup = {}; // expenseId -> groupNumber
        let groupNum = 1;
        Object.entries(urlToIds).forEach(([url, ids]) => {
            if (ids.length > 1) {
                groups[url] = { number: groupNum, memberIds: ids, receipt_url: url };
                ids.forEach(id => { idToGroup[id] = groupNum; });
                groupNum++;
            }
        });
        return { groups, idToGroup, list: Object.values(groups) };
    }, [expenses]);

    // Toggle selection for an entire group
    const toggleGroupSelection = (memberIds) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            const allSelected = memberIds.every(id => next.has(id));
            if (allSelected) {
                memberIds.forEach(id => next.delete(id));
            } else {
                memberIds.forEach(id => next.add(id));
            }
            return next;
        });
    };

    // SVG Icons
    const IconCheck = ({ size = 14, color = '#2e7d32' }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
    const IconPaperclip = ({ size = 14, color = '#bbb' }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
        </svg>
    );
    const IconLink = ({ size = 14, color = '#1565c0' }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
        </svg>
    );
    const IconDash = ({ size = 14, color = '#ccc' }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
            <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
    );
    const IconScissors = ({ size = 14, color = '#c62828' }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="6" cy="6" r="3" />
            <circle cx="6" cy="18" r="3" />
            <line x1="20" y1="4" x2="8.12" y2="15.88" />
            <line x1="14.47" y1="14.48" x2="20" y2="20" />
            <line x1="8.12" y1="8.12" x2="12" y2="12" />
        </svg>
    );

    if (!hasAccess) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                color: '#666'
            }}>
                접근 권한이 없습니다.
            </div>
        );
    }

    return (
        <div ref={pageRef} style={styles.page}>
            {/* Header */}
            <h1 style={styles.header}>Dépenses</h1>

            {/* Year / Month Picker */}
            <div style={styles.pickerRow}>
                {/* Year */}
                <div style={styles.pickerBox}>
                    <button onClick={() => setSelectedYear(y => y - 1)} style={styles.pickerBtn}>◀</button>
                    <span style={styles.yearLabel}>{selectedYear}</span>
                    <button
                        onClick={() => setSelectedYear(y => Math.min(y + 1, currentYear))}
                        disabled={selectedYear >= currentYear}
                        style={{ ...styles.pickerBtn, opacity: selectedYear >= currentYear ? 0.3 : 1 }}
                    >▶</button>
                </div>

                {/* Month scroll */}
                <div style={styles.monthScroll}>
                    {MONTHS_FR_SHORT.map((m, i) => {
                        const status = monthStatus[i + 1];
                        const flags = [
                            status?.needsReview && `${status.unverified} opération${status.unverified > 1 ? 's' : ''} à vérifier`,
                            status?.needsReissue && 'Période réouverte · à réémettre'
                        ].filter(Boolean);
                        return (
                            <button
                                key={i}
                                onClick={() => setSelectedMonth(i + 1)}
                                className={status?.needsReissue ? pageStyles.monthNeedsReissue : undefined}
                                title={flags.length > 0 ? flags.join('\n') : undefined}
                                style={{
                                    ...styles.monthBtn,
                                    fontWeight: selectedMonth === i + 1 ? '700' : '400',
                                    backgroundColor: selectedMonth === i + 1 ? '#333' : 'transparent',
                                    color: selectedMonth === i + 1 ? '#fff' : '#666',
                                }}
                            >
                                {m}
                                {status?.needsReview && (
                                    <span className={pageStyles.monthBadge} aria-label={flags[0]}>
                                        {status.unverified}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Upload Zone (shown when no data) */}
            {expenses.length === 0 && !isLoading && (
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    style={{
                        ...styles.dropZone,
                        borderColor: isDragOver ? '#333' : '#ccc',
                        backgroundColor: isDragOver ? '#f5f5f0' : '#fff',
                    }}
                >
                    {isUploading ? (
                        <>
                            <div style={{ fontSize: '36px', marginBottom: '8px' }}>⏳</div>
                            <div style={styles.dropLabel}>Traitement en cours...</div>
                        </>
                    ) : (
                        <>
                            <div style={{ fontSize: '36px', marginBottom: '8px' }}>📄</div>
                            <div style={styles.dropLabel}>Déposez vos relevés de compte ici</div>
                            <div style={styles.dropSub}>Fichiers PDF — Crédit Agricole relevé de compte</div>
                            <div style={styles.dropHint}>Plusieurs fichiers acceptés</div>
                        </>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload(e.target.files)}
                    />
                </div>
            )}

            {/* Statements info + Upload more */}
            {statements.length > 0 && (
                <div style={styles.statementsRow}>
                    {statements.map(s => (
                        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <button onClick={() => handleDownloadReleve(s)} style={styles.statementBtn}>
                                📄 {s.original_filename.length > 30 ? s.original_filename.substring(0, 30) + '...' : s.original_filename}
                            </button>
                            <button
                                onClick={() => handleDeleteStatement(s.id, s.original_filename)}
                                style={styles.statementDeleteBtn}
                                title="Supprimer ce relevé"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        style={styles.addPdfBtn}
                    >
                        + Ajouter PDF
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload(e.target.files)}
                    />
                </div>
            )}

            {/* Loading */}
            {isLoading && (
                <div style={styles.emptyState}>Chargement...</div>
            )}

            {/* Bank credit grouping toolbar */}
            {creditGroupMode && (
                <div style={styles.creditGroupBar}>
                    <span style={{ fontSize: '12px' }}>
                        {selectedCreditIds.size} crédit(s) · {formatAmount(selectedCreditTotal)}€
                    </span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={cancelCreditGroupMode} style={styles.batchCancelBtn}>Annuler</button>
                        <button
                            onClick={openSelectedCreditGroup}
                            disabled={selectedCreditIds.size < 2}
                            style={{
                                ...styles.creditGroupActionBtn,
                                opacity: selectedCreditIds.size >= 2 ? 1 : 0.45,
                                cursor: selectedCreditIds.size >= 2 ? 'pointer' : 'not-allowed'
                            }}
                        >
                            Associer des paiements
                        </button>
                    </div>
                </div>
            )}

            {/* Batch mode toolbar */}
            {batchMode && (
                <div style={{ marginBottom: '8px' }}>
                    <div style={styles.batchBar}>
                        <span style={{ fontSize: '12px' }}>
                            {selectedIds.size} sélectionné(s)
                        </span>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <button onClick={cancelBatchMode} style={styles.batchCancelBtn}>
                                Annuler
                            </button>
                            {/* Lier: link selected to shared receipt */}
                            {(() => {
                                const hasReceiptInSelection = expenses.some(e => selectedIds.has(e.id) && (e.has_receipt || e.receipt_url));
                                const hasNonReceiptInSelection = expenses.some(e => selectedIds.has(e.id) && !e.has_receipt && !e.receipt_url);
                                const canLink = hasReceiptInSelection && hasNonReceiptInSelection && selectedIds.size >= 2;
                                return canLink ? (
                                    <button onClick={handleLinkReceipt} style={styles.batchLinkBtn}>
                                        <IconLink size={12} color="#fff" /> Lier
                                    </button>
                                ) : null;
                            })()}
                            {/* Délier: unlink selected from group */}
                            {(() => {
                                const hasGroupedInSelection = Array.from(selectedIds).some(id => receiptGroups.idToGroup[id]);
                                return hasGroupedInSelection ? (
                                    <button onClick={handleUnlinkReceipt} style={styles.batchUnlinkBtn}>
                                        <IconScissors size={12} color="#fff" /> Délier
                                    </button>
                                ) : null;
                            })()}
                            <label style={{
                                ...styles.batchApplyBtn,
                                opacity: selectedIds.size > 0 ? 1 : 0.5,
                                cursor: selectedIds.size > 0 ? 'pointer' : 'not-allowed'
                            }}>
                                <IconPaperclip size={12} color="#fff" /> Attacher
                                <input
                                    ref={receiptInputRef}
                                    type="file"
                                    accept="image/*,.pdf"
                                    style={{ display: 'none' }}
                                    disabled={selectedIds.size === 0}
                                    onChange={(e) => {
                                        if (e.target.files[0]) {
                                            handleBatchReceiptUpload(e.target.files[0]);
                                        }
                                    }}
                                />
                            </label>
                            {/* N/A: mark as no receipt needed */}
                            {selectedIds.size > 0 && (
                                <button onClick={handleMarkNA} style={styles.batchCpaBtn}>
                                    N/A
                                </button>
                            )}
                            {/* Un-N/A */}
                            {(() => {
                                const hasCpaInSelection = Array.from(selectedIds).some(id => {
                                    const exp = expenses.find(e => e.id === id);
                                    return exp && exp.cpa_exempt;
                                });
                                return hasCpaInSelection ? (
                                    <button onClick={handleUnmarkNA} style={styles.batchUnCpaBtn}>
                                        ✕ N/A
                                    </button>
                                ) : null;
                            })()}
                        </div>
                    </div>
                    {/* Group chips */}
                    {receiptGroups.list.length > 0 && (
                        <div style={styles.groupChipsRow}>
                            <span style={{ fontSize: '10px', color: '#999', marginRight: '4px' }}>Groupes :</span>
                            {receiptGroups.list.map(g => {
                                const allSelected = g.memberIds.every(id => selectedIds.has(id));
                                return (
                                    <button
                                        key={g.number}
                                        onClick={() => toggleGroupSelection(g.memberIds)}
                                        style={{
                                            ...styles.groupChip,
                                            backgroundColor: allSelected ? '#1565c0' : '#fff',
                                            color: allSelected ? '#fff' : '#1565c0',
                                            borderColor: '#1565c0'
                                        }}
                                    >
                                        G{g.number} ({g.memberIds.length})
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {/* N/A chip */}
                    {(() => {
                        const cpaExpenses = expenses.filter(e => e.cpa_exempt);
                        if (cpaExpenses.length === 0) return null;
                        const cpaIds = cpaExpenses.map(e => e.id);
                        const allSelected = cpaIds.every(id => selectedIds.has(id));
                        return (
                            <div style={{ ...styles.groupChipsRow, paddingTop: receiptGroups.list.length > 0 ? '0' : '6px' }}>
                                <span style={{ fontSize: '10px', color: '#999', marginRight: '4px' }}>Statut :</span>
                                <button
                                    onClick={() => toggleGroupSelection(cpaIds)}
                                    style={{
                                        ...styles.groupChip,
                                        backgroundColor: allSelected ? '#7b1fa2' : '#fff',
                                        color: allSelected ? '#fff' : '#7b1fa2',
                                        borderColor: '#7b1fa2'
                                    }}
                                >
                                    N/A ({cpaIds.length})
                                </button>
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* Expense Table */}
            {!isLoading && expenses.length > 0 && (
                <>
                    {/* Action Buttons (above table) */}
                    <div style={styles.actionRow}>
                        {!batchMode && !creditGroupMode && (
                            <button onClick={() => setBatchMode(true)} style={styles.actionBtn}>
                                ☑ Sélection multiple
                            </button>
                        )}
                        {!batchMode && !creditGroupMode && (
                            <button onClick={() => setCreditGroupMode(true)} style={styles.creditGroupModeBtn}>
                                Regrouper des crédits
                            </button>
                        )}
                        <button
                            onClick={handleDownloadAllReceipts}
                            disabled={receiptCount === 0}
                            style={{
                                ...styles.actionBtnPrimary,
                                opacity: receiptCount > 0 ? 1 : 0.5,
                                cursor: receiptCount > 0 ? 'pointer' : 'not-allowed'
                            }}
                        >
                            📦 Justificatifs ZIP ({receiptCount})
                        </button>
                        <button
                            onClick={openExportModal}
                            style={styles.exportBtn}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                            Exporter
                        </button>
                    </div>

                    <div ref={tableViewportRef} style={{ overflowX: 'auto', marginBottom: '16px' }}>
                        <table className={pageStyles.table} style={styles.table}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8f8f5' }}>
                                    {(batchMode || creditGroupMode) && <th style={{ ...styles.th, width: '30px' }}></th>}
                                    <th style={styles.th}>Date</th>
                                    <th className={pageStyles.descCell} style={{ ...styles.th, textAlign: 'left' }}>Libellé</th>
                                    <th className={pageStyles.desktopOnly} style={{ ...styles.th, textAlign: 'right', color: '#c62828' }}>Débit</th>
                                    <th className={pageStyles.desktopOnly} style={{ ...styles.th, textAlign: 'right', color: '#2e7d32' }}>Crédit</th>
                                    <th className={pageStyles.mobileOnly} style={{ ...styles.th, textAlign: 'right' }}>Montant</th>
                                    <th style={{ ...styles.th, width: '36px', textAlign: 'center' }}><IconPaperclip size={12} color="#999" /></th>
                                    <th style={{ ...styles.th, width: '58px', textAlign: 'center' }}>Vérifié</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.map((exp, i) => {
                                    const hasReceipt = exp.has_receipt || !!exp.receipt_url;
                                    const isShared = exp.receipt_url && receiptGroups.idToGroup[exp.id];
                                    const isCPA = !!exp.cpa_exempt;
                                    const isSelected = selectedIds.has(exp.id);
                                    const isCreditGroupSelected = selectedCreditIds.has(exp.id);
                                    const canSelectForCreditGroup = parseAmount(exp.credit) > 0 && !exp.payment_group_id;
                                    const isFinalChecked = !!exp.final_checked;
                                    const isSavingFinalCheck = savingFinalCheckIds.has(exp.id);
                                    const creditContent = exp.credit ? (
                                        batchMode || creditGroupMode ? (
                                            <>
                                                {formatAmount(exp.credit)}
                                                {creditGroupMode && exp.payment_group_id && (
                                                    <span className={paymentMatchStyles.creditGroupBadge}>Déjà groupé</span>
                                                )}
                                            </>
                                        ) : (
                                            <button
                                                type="button"
                                                className={paymentMatchStyles.creditButton}
                                                onClick={(e) => { e.stopPropagation(); openPaymentMatchModal(e, exp); }}
                                                title="Associer des paiements"
                                            >
                                                {formatAmount(exp.credit)}
                                                {exp.payment_group_id && (
                                                    <span className={paymentMatchStyles.creditGroupBadge}>
                                                        Groupe · {exp.payment_group_credit_count} crédits · {formatAmount(exp.payment_group_total_credit)}€
                                                    </span>
                                                )}
                                                {Number(exp.payment_match_count) > 0 && (
                                                    <span className={paymentMatchStyles.matchBadge}>{exp.payment_match_count} paiement{Number(exp.payment_match_count) > 1 ? 's' : ''}</span>
                                                )}
                                            </button>
                                        )
                                    ) : '';
                                    return (
                                        <tr
                                            key={exp.id}
                                            data-expense-id={exp.id}
                                            style={{
                                                borderBottom: '1px solid #f0f0f0',
                                                backgroundColor: isSelected || isCreditGroupSelected
                                                    ? styles.selectedRow.backgroundColor
                                                    : isFinalChecked
                                                        ? styles.verifiedRow.backgroundColor
                                                        : (i % 2 === 0 ? '#fff' : '#fdfdfb'),
                                                boxShadow: isFinalChecked ? styles.verifiedRow.boxShadow : 'none',
                                                cursor: creditGroupMode && !canSelectForCreditGroup ? 'not-allowed' : 'pointer'
                                            }}
                                            onClick={batchMode
                                                ? () => toggleSelection(exp.id)
                                                : creditGroupMode
                                                    ? () => toggleCreditGroupSelection(exp)
                                                    : () => {
                                                        if (expandedExpenseId === exp.id) {
                                                            setExpandedExpenseId(null);
                                                        } else {
                                                            setExpandedExpenseId(exp.id);
                                                            setNoteText(exp.note || '');
                                                        }
                                                    }}
                                        >
                                            {(batchMode || creditGroupMode) && (
                                                <td style={{ ...styles.td, textAlign: 'center', padding: '4px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={creditGroupMode ? isCreditGroupSelected : isSelected}
                                                        disabled={creditGroupMode && !canSelectForCreditGroup}
                                                        onChange={() => creditGroupMode ? toggleCreditGroupSelection(exp) : toggleSelection(exp.id)}
                                                        onClick={(event) => event.stopPropagation()}
                                                        style={{ cursor: creditGroupMode && !canSelectForCreditGroup ? 'not-allowed' : 'pointer' }}
                                                    />
                                                </td>
                                            )}
                                            <td style={{ ...styles.td, fontFamily: 'monospace', whiteSpace: 'nowrap', color: '#888' }}>
                                                {formatDate(exp.date_operation)}
                                            </td>
                                            <td className={pageStyles.descCell} style={styles.td}>
                                                <div className={pageStyles.descText} title={exp.description}>
                                                    {exp.description}
                                                </div>
                                                {/* Always show existing note */}
                                                {exp.note && expandedExpenseId !== exp.id && (
                                                    <div style={styles.notePreview}>
                                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                                                        {exp.note}
                                                    </div>
                                                )}
                                                {/* Expanded: editable textarea */}
                                                {expandedExpenseId === exp.id && !batchMode && (
                                                    <div style={styles.noteEditArea} onClick={(e) => e.stopPropagation()}>
                                                        <textarea
                                                            value={noteText}
                                                            onChange={(e) => setNoteText(e.target.value)}
                                                            placeholder="Ajouter un mémo pour le comptable..."
                                                            style={styles.noteTextarea}
                                                            rows={2}
                                                            autoFocus
                                                        />
                                                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                                                            <button
                                                                onClick={() => setExpandedExpenseId(null)}
                                                                style={styles.noteCancelBtn}
                                                            >
                                                                Annuler
                                                            </button>
                                                            <button
                                                                onClick={() => handleSaveNote(exp.id)}
                                                                style={styles.noteSaveBtn}
                                                            >
                                                                Enregistrer
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className={pageStyles.desktopOnly} style={{
                                                ...styles.td,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                                color: exp.debit ? '#c62828' : 'transparent'
                                            }}>
                                                {exp.debit ? formatAmount(exp.debit) : ''}
                                            </td>
                                            <td className={pageStyles.desktopOnly} style={{
                                                ...styles.td,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                                color: exp.credit ? '#2e7d32' : 'transparent'
                                            }}>
                                                {creditContent}
                                            </td>
                                            <td className={pageStyles.mobileOnly} style={{
                                                ...styles.td,
                                                textAlign: 'right',
                                                fontFamily: 'monospace',
                                                color: exp.debit ? '#c62828' : '#2e7d32'
                                            }}>
                                                {exp.debit ? formatAmount(exp.debit) : creditContent}
                                            </td>
                                            <td style={{ ...styles.td, textAlign: 'center', padding: '4px' }}>
                                                {batchMode ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                                        {isCPA
                                                            ? <span style={styles.cpaBadge}>N/A</span>
                                                            : hasReceipt
                                                                ? (isShared
                                                                    ? <><IconLink size={13} /><span style={styles.groupBadge}>G{receiptGroups.idToGroup[exp.id]}</span></>
                                                                    : <IconCheck size={14} />
                                                                )
                                                                : <IconDash size={14} style={{ opacity: 0.3 }} />
                                                        }
                                                    </span>
                                                ) : (
                                                    isCPA ? (
                                                        <span style={styles.cpaBadge}>N/A</span>
                                                    ) : hasReceipt ? (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setPreviewExpense(exp); }}
                                                            title={isShared ? 'Justificatif partagé' : 'Voir le justificatif'}
                                                            style={styles.receiptBtn}
                                                        >
                                                            {isShared
                                                                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}><IconLink size={14} /><span style={styles.groupBadge}>G{receiptGroups.idToGroup[exp.id]}</span></span>
                                                                : <IconCheck size={15} />
                                                            }
                                                        </button>
                                                    ) : (
                                                        <label
                                                            style={{ ...styles.receiptLabel }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <IconPaperclip size={14} />
                                                            <input
                                                                type="file"
                                                                accept="image/*,.pdf"
                                                                style={{ display: 'none' }}
                                                                onChange={(e) => {
                                                                    if (e.target.files[0]) {
                                                                        handleReceiptUpload(exp.id, e.target.files[0]);
                                                                    }
                                                                }}
                                                            />
                                                        </label>
                                                    )
                                                )}
                                            </td>
                                            <td style={{ ...styles.td, textAlign: 'center', padding: '4px' }}>
                                                {batchMode ? (
                                                    isFinalChecked
                                                        ? <IconCheck size={15} />
                                                        : <span style={styles.finalCheckEmpty}>○</span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleFinalCheck(exp);
                                                        }}
                                                        disabled={isSavingFinalCheck}
                                                        aria-label={isFinalChecked ? 'Annuler la vérification' : 'Vérifier'}
                                                        title={isFinalChecked ? 'Annuler la vérification' : 'Marquer comme vérifié'}
                                                        style={{
                                                            ...styles.finalCheckBtn,
                                                            opacity: isSavingFinalCheck ? 0.45 : 1
                                                        }}
                                                    >
                                                        {isFinalChecked
                                                            ? <IconCheck size={15} />
                                                            : <span style={styles.finalCheckEmpty}>○</span>
                                                        }
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Summary */}
                    <div style={styles.summaryCard}>
                        <div style={styles.summaryRow}>
                            <span style={{ color: '#c62828', fontWeight: '600' }}>Total Débit</span>
                            <span style={{ fontFamily: 'monospace', color: '#c62828' }}>
                                {formatAmount(computedSummary.totalDebit)}€
                            </span>
                        </div>
                        <div style={styles.summaryRow}>
                            <span style={{ color: '#2e7d32', fontWeight: '600' }}>Total Crédit</span>
                            <span style={{ fontFamily: 'monospace', color: '#2e7d32' }}>
                                {formatAmount(computedSummary.totalCredit)}€
                            </span>
                        </div>
                        <div style={styles.summaryTotal}>
                            <span style={{ fontWeight: '700' }}>Solde</span>
                            <span style={{
                                fontFamily: 'monospace',
                                fontWeight: '700',
                                color: computedSummary.balance >= 0 ? '#2e7d32' : '#c62828'
                            }}>
                                {computedSummary.balance >= 0 ? '+' : ''}{formatAmount(computedSummary.balance)}€
                            </span>
                        </div>
                        <div style={styles.summaryMeta}>
                            <span>{expenses.length} opérations</span>
                            <span>{receiptCount}/{expenses.length} justificatifs</span>
                            <span>{verifiedCount}/{expenses.length} vérifiées</span>
                        </div>
                    </div>
                </>
            )}

            {/* Empty state */}
            {!isLoading && expenses.length === 0 && statements.length === 0 && (
                <div style={styles.emptyState}>
                    Aucune donnée pour {MONTHS_FR[selectedMonth - 1]} {selectedYear}
                </div>
            )}

            {/* Credit payment matching modal */}
            {paymentMatchExpenses.length > 0 && (
                <ExpensePaymentMatchModal
                    expenses={paymentMatchExpenses}
                    onClose={() => setPaymentMatchExpenses([])}
                    onSaved={refreshAfterPaymentMatch}
                />
            )}

            {/* Receipt Preview Modal */}
            {previewExpense && (
                <div style={styles.modalOverlay} onClick={() => setPreviewExpense(null)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontSize: '11px', color: '#888', flex: 1 }}>
                                {formatDate(previewExpense.date_operation)} — {previewExpense.description?.substring(0, 40)}
                            </div>
                            <button onClick={() => setPreviewExpense(null)} style={styles.modalCloseBtn}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            {!previewUrl ? (
                                <div style={{ color: '#999', fontSize: '12px', padding: '40px' }}>Chargement...</div>
                            ) : previewExpense.receipt_url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                <img
                                    src={previewUrl}
                                    alt="Justificatif"
                                    style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: '4px' }}
                                />
                            ) : (
                                <iframe
                                    src={previewUrl}
                                    title="Justificatif PDF"
                                    style={{ width: '100%', height: '60vh', border: 'none', borderRadius: '4px' }}
                                />
                            )}
                        </div>
                        <div style={styles.modalFooter}>
                            <span style={{ fontSize: '10px', color: '#999' }}>
                                {previewExpense.receipt_filename || 'justificatif'}
                            </span>
                            <button
                                onClick={async () => {
                                    await handleReceiptDelete(previewExpense.id);
                                    setPreviewExpense(null);
                                }}
                                style={styles.modalDeleteBtn}
                            >
                                🗑 Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Statement Preview Modal */}
            {previewStatement && (
                <div style={styles.modalOverlay} onClick={() => { setPreviewStatement(null); if (previewStatementUrl?.startsWith('blob:')) URL.revokeObjectURL(previewStatementUrl); setPreviewStatementUrl(null); }}>
                    <div style={{ ...styles.modalContent, maxWidth: '700px', width: '95vw' }} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontSize: '11px', color: '#888', flex: 1 }}>
                                📄 {previewStatement.original_filename}
                            </div>
                            <button onClick={() => { setPreviewStatement(null); if (previewStatementUrl?.startsWith('blob:')) URL.revokeObjectURL(previewStatementUrl); setPreviewStatementUrl(null); }} style={styles.modalCloseBtn}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            {!previewStatementUrl ? (
                                <div style={{ color: '#999', fontSize: '12px', padding: '40px', textAlign: 'center' }}>Chargement du relevé...</div>
                            ) : previewStatementUrl === 'error' ? (
                                <div style={{ padding: '40px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
                                    <div style={{ fontSize: '13px', color: '#666', fontWeight: '600', marginBottom: '8px' }}>Aperçu non disponible</div>
                                    <div style={{ fontSize: '11px', color: '#999' }}>L'endpoint de téléchargement n'est pas encore configuré sur le serveur.</div>
                                </div>
                            ) : (
                                <iframe
                                    src={previewStatementUrl}
                                    title="Relevé de compte"
                                    style={{ width: '100%', height: '70vh', border: 'none', borderRadius: '4px' }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Export Modal */}
            {showExportModal && (
                <div style={styles.modalOverlay} onClick={() => { if (exportProgress === null) setShowExportModal(false); }}>
                    <div style={{ ...styles.modalContent, ...styles.exportModalContent }} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontSize: '13px', fontWeight: '700', flex: 1 }}>Exporter les données</div>
                            {exportProgress === null && (
                                <button onClick={() => setShowExportModal(false)} style={styles.modalCloseBtn}>✕</button>
                            )}
                        </div>
                        <div style={styles.exportModalBody}>
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '12px' }}>
                                {MONTHS_FR[selectedMonth - 1]} {selectedYear} — Sélectionnez les éléments à exporter
                            </div>

                            {/* Checkboxes */}
                            <label style={styles.exportCheckbox}>
                                <input
                                    type="checkbox"
                                    checked={exportOptions.summary}
                                    onChange={(e) => setExportOptions(p => ({ ...p, summary: e.target.checked }))}
                                    disabled={exportProgress !== null}
                                />
                                <div>
                                    <div style={{ fontWeight: '600' }}>Récapitulatif PDF</div>
                                    <div style={{ fontSize: '10px', color: '#999' }}>Liste des écritures avec mémos, statuts N/A et groupes</div>
                                </div>
                            </label>

                            {canExportFacture && (
                                <div style={styles.exportInvoiceBlock}>
                                    <label style={{ ...styles.exportCheckbox, marginBottom: 0 }}>
                                        <input
                                            type="checkbox"
                                            checked={exportOptions.invoices}
                                            onChange={(e) => setExportOptions(p => ({ ...p, invoices: e.target.checked }))}
                                            disabled={exportProgress !== null || exportDataLoading || Boolean(exportDataError)}
                                        />
                                        <div>
                                            <div style={{ fontWeight: '600' }}>Factures des crédits</div>
                                            <div style={{ fontSize: '10px', color: '#999' }}>
                                                {exportDataLoading
                                                    ? 'Chargement des rapprochements...'
                                                    : `${exportData.summary?.invoice_count || 0} facture(s) — valeurs modifiées prioritaires`}
                                            </div>
                                        </div>
                                    </label>

                                    {!exportDataLoading && !exportDataError && (
                                        <div style={styles.invoicePeriodStatus}>
                                            <span>
                                                {invoicePeriod.status === 'issued'
                                                    ? `Factures émises · Version ${invoicePeriod.current_version}`
                                                    : invoicePeriod.status === 'reopened'
                                                        ? `Période réouverte · prochaine version ${Number(invoicePeriod.current_version) + 1}`
                                                        : 'Brouillon · numéros non émis'}
                                            </span>
                                            {invoicePeriod.status === 'issued' && (
                                                <button
                                                    type="button"
                                                    onClick={reopenInvoicePeriod}
                                                    disabled={invoicePeriodUpdating || exportProgress !== null}
                                                    style={styles.invoicePeriodAction}
                                                >
                                                    {invoicePeriodUpdating ? 'Réouverture...' : 'Réouvrir'}
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {exportOptions.invoices && !exportDataLoading && !exportDataError && (
                                        <div style={styles.exportInvoiceOptions}>
                                            {INVOICE_TYPE_OPTIONS.map(([type, label]) => (
                                                <label key={type} style={styles.exportMiniCheckbox}>
                                                    <input
                                                        type="checkbox"
                                                        checked={exportOptions.invoiceTypes[type]}
                                                        onChange={(e) => setExportOptions(p => ({
                                                            ...p,
                                                            invoiceTypes: { ...p.invoiceTypes, [type]: e.target.checked }
                                                        }))}
                                                        disabled={exportProgress !== null || Number(invoiceCounts[type]) === 0}
                                                    />
                                                    <span>{label} ({invoiceCounts[type] || 0})</span>
                                                </label>
                                            ))}
                                            <div style={styles.exportInvoiceSeparator} />
                                            <label style={styles.exportMiniCheckbox}>
                                                <input
                                                    type="checkbox"
                                                    checked={exportOptions.invoiceList}
                                                    onChange={(e) => setExportOptions(p => ({ ...p, invoiceList: e.target.checked }))}
                                                    disabled={exportProgress !== null}
                                                />
                                                <span>Liste récapitulative PDF</span>
                                            </label>
                                            <label style={styles.exportMiniCheckbox}>
                                                <input
                                                    type="checkbox"
                                                    checked={exportOptions.invoiceFiles}
                                                    onChange={(e) => setExportOptions(p => ({ ...p, invoiceFiles: e.target.checked }))}
                                                    disabled={exportProgress !== null}
                                                />
                                                <span>Factures individuelles</span>
                                            </label>
                                            <div style={styles.exportInvoiceSeparator} />
                                            {INVOICE_EXPORT_SCOPE_OPTIONS.map(([scope, label]) => (
                                                <label key={scope} style={styles.exportMiniCheckbox}>
                                                    <input
                                                        type="radio"
                                                        name="invoiceScope"
                                                        checked={exportOptions.invoiceScope === scope}
                                                        onChange={() => setExportOptions(p => ({ ...p, invoiceScope: scope }))}
                                                        disabled={exportProgress !== null}
                                                    />
                                                    <span>{label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}

                                    {exportDataError && <div style={styles.exportError}>{exportDataError}</div>}
                                    {!exportDataLoading && !exportDataError && exportData.summary && (
                                        <>
                                            {exportData.summary.unassociated_credit_count > 0 && (
                                                <div style={styles.exportWarning}>
                                                    {exportData.summary.unassociated_credit_count} crédit(s) sans paiement associé
                                                </div>
                                            )}
                                            {exportData.summary.mismatch_count > 0 && (
                                                <div style={styles.exportWarning}>
                                                    {exportData.summary.mismatch_count} rapprochement(s) avec un écart de montant
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            <label style={styles.exportCheckbox}>
                                <input
                                    type="checkbox"
                                    checked={exportOptions.receipts}
                                    onChange={(e) => setExportOptions(p => ({ ...p, receipts: e.target.checked }))}
                                    disabled={exportProgress !== null}
                                />
                                <div>
                                    <div style={{ fontWeight: '600' }}>Justificatifs</div>
                                    <div style={{ fontSize: '10px', color: '#999' }}>{receiptCount} fichier(s) — dossier Justificatifs/</div>
                                </div>
                            </label>

                            <label style={styles.exportCheckbox}>
                                <input
                                    type="checkbox"
                                    checked={exportOptions.statements}
                                    onChange={(e) => setExportOptions(p => ({ ...p, statements: e.target.checked }))}
                                    disabled={exportProgress !== null}
                                />
                                <div>
                                    <div style={{ fontWeight: '600' }}>Relevés de compte</div>
                                    <div style={{ fontSize: '10px', color: '#999' }}>{statements.length} relevé(s) originaux — dossier Relevés/</div>
                                </div>
                            </label>

                            {/* Progress */}
                            {exportProgress !== null && (
                                <div style={{ marginTop: '12px' }}>
                                    <div style={styles.progressBar}>
                                        <div style={{ ...styles.progressFill, width: `${exportProgress}%` }} />
                                    </div>
                                    <div style={{ fontSize: '10px', color: '#888', textAlign: 'center', marginTop: '4px' }}>
                                        {exportProgress < 100 ? `Export en cours... ${exportProgress}%` : 'Terminé !'}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={styles.exportModalFooter}>
                            {exportProgress === null ? (
                                <>
                                    <button
                                        onClick={() => setShowExportModal(false)}
                                        style={styles.noteCancelBtn}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        disabled={!hasAnyExportOption || exportDataLoading}
                                        onClick={async () => {
                                            setExportProgress(0);
                                            try {
                                                let effectiveExportData = exportData;
                                                if (canExportFacture && exportOptions.invoices &&
                                                    (selectedInvoiceCount > 0 || invoicePeriod.status === 'reopened') &&
                                                    (exportOptions.invoiceList || exportOptions.invoiceFiles)) {
                                                    const issuance = await issueAccountingInvoices(selectedYear, selectedMonth);
                                                    effectiveExportData = issuance.export_data;
                                                    setExportData(issuance.export_data);
                                                    setInvoicePeriod(issuance.period);
                                                }
                                                await exportExpenses({
                                                    expenses,
                                                    statements,
                                                    year: selectedYear,
                                                    month: selectedMonth,
                                                    receiptGroups,
                                                    exportData: effectiveExportData,
                                                    include: {
                                                        ...exportOptions,
                                                        invoices: canExportFacture && exportOptions.invoices
                                                    },
                                                    onProgress: setExportProgress
                                                });
                                                setTimeout(() => {
                                                    setShowExportModal(false);
                                                    setExportProgress(null);
                                                }, 1200);
                                            } catch (err) {
                                                console.error('Export error:', err);
                                                alert(err?.message || 'Erreur lors de l\'export.');
                                                setExportProgress(null);
                                            }
                                        }}
                                        style={{
                                            ...styles.noteSaveBtn,
                                            padding: '6px 20px',
                                            fontSize: '12px',
                                            opacity: (!hasAnyExportOption || exportDataLoading) ? 0.4 : 1
                                        }}
                                    >
                                        Télécharger ZIP
                                    </button>
                                </>
                            ) : exportProgress >= 100 ? (
                                <button
                                    onClick={() => { setShowExportModal(false); setExportProgress(null); }}
                                    style={{ ...styles.noteSaveBtn, padding: '6px 20px', fontSize: '12px' }}
                                >
                                    Fermer
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============ Styles ============
const styles = {
    page: {
        backgroundColor: '#fffdf8',
        minHeight: '100vh',
        padding: '20px 16px 120px',
        maxWidth: '800px',
        margin: '0 auto'
    },
    header: {
        fontSize: '18px',
        fontWeight: '700',
        textAlign: 'center',
        margin: '0 0 20px',
        textTransform: 'uppercase',
        letterSpacing: '2px',
        color: '#333'
    },
    pickerRow: {
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '20px'
    },
    pickerBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '4px 8px'
    },
    pickerBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '10px',
        padding: '4px',
        color: '#666'
    },
    yearLabel: {
        fontSize: '14px',
        fontWeight: '600',
        minWidth: '50px',
        textAlign: 'center'
    },
    monthScroll: {
        display: 'flex',
        gap: '2px',
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '4px',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
    },
    monthBtn: {
        padding: '4px 10px',
        fontSize: '11px',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 0.15s'
    },
    dropZone: {
        border: '2px dashed #ccc',
        borderRadius: '12px',
        padding: '40px 20px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s',
        marginBottom: '20px'
    },
    dropLabel: { fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '4px' },
    dropSub: { fontSize: '11px', color: '#999' },
    dropHint: { fontSize: '10px', color: '#bbb', marginTop: '4px' },
    statementsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '12px'
    },
    statementBtn: {
        padding: '4px 10px',
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '6px',
        fontSize: '10px',
        cursor: 'pointer',
        color: '#555',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    addPdfBtn: {
        padding: '4px 10px',
        backgroundColor: '#f5f5f5',
        border: '1px dashed #ccc',
        borderRadius: '6px',
        fontSize: '10px',
        cursor: 'pointer',
        color: '#999'
    },
    statementDeleteBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '10px',
        color: '#ccc',
        padding: '2px 4px',
        borderRadius: '4px',
        lineHeight: 1
    },
    batchBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        backgroundColor: '#e8f5e9',
        borderRadius: '8px',
        marginBottom: '8px'
    },
    creditGroupBar: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: '#eef4fb',
        border: '1px solid #c9d9eb',
        borderRadius: '8px',
        marginBottom: '8px'
    },
    creditGroupActionBtn: {
        padding: '4px 12px',
        backgroundColor: '#315b82',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '600'
    },
    batchCancelBtn: {
        padding: '4px 12px',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '6px',
        fontSize: '11px',
        cursor: 'pointer',
        color: '#555'
    },
    batchApplyBtn: {
        padding: '4px 12px',
        backgroundColor: '#333',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '11px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
    },
    batchLinkBtn: {
        padding: '4px 12px',
        backgroundColor: '#1565c0',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '11px',
        cursor: 'pointer',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
    },
    batchUnlinkBtn: {
        padding: '4px 12px',
        backgroundColor: '#c62828',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '11px',
        cursor: 'pointer',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
    },
    groupChipsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '6px 12px',
        flexWrap: 'wrap'
    },
    groupChip: {
        padding: '2px 10px',
        border: '1px solid #1565c0',
        borderRadius: '12px',
        fontSize: '10px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.15s',
        letterSpacing: '0.3px'
    },
    groupBadge: {
        fontSize: '8px',
        fontWeight: '700',
        color: '#1565c0',
        letterSpacing: '0.2px'
    },
    cpaBadge: {
        fontSize: '8px',
        fontWeight: '700',
        color: '#7b1fa2',
        backgroundColor: '#f3e5f5',
        padding: '1px 5px',
        borderRadius: '4px',
        letterSpacing: '0.5px'
    },
    finalCheckBtn: {
        width: '28px',
        height: '28px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        background: 'transparent',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'opacity 0.15s, background-color 0.15s'
    },
    finalCheckEmpty: {
        color: '#bbb',
        fontSize: '18px',
        lineHeight: 1
    },
    batchCpaBtn: {
        padding: '4px 10px',
        backgroundColor: '#7b1fa2',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '10px',
        fontWeight: '700',
        cursor: 'pointer',
        letterSpacing: '0.5px'
    },
    batchUnCpaBtn: {
        padding: '4px 10px',
        backgroundColor: '#fff',
        color: '#7b1fa2',
        border: '1px solid #7b1fa2',
        borderRadius: '6px',
        fontSize: '10px',
        fontWeight: '700',
        cursor: 'pointer',
        letterSpacing: '0.5px'
    },
    notePreview: {
        fontSize: '10px',
        color: '#888',
        marginTop: '2px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '3px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
    },
    noteEditArea: {
        marginTop: '6px',
        padding: '6px 0'
    },
    noteTextarea: {
        width: '100%',
        padding: '6px 8px',
        fontSize: '11px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        resize: 'vertical',
        fontFamily: 'inherit',
        marginBottom: '4px',
        outline: 'none',
        boxSizing: 'border-box'
    },
    noteCancelBtn: {
        padding: '3px 10px',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '10px',
        cursor: 'pointer',
        color: '#666'
    },
    noteSaveBtn: {
        padding: '3px 10px',
        backgroundColor: '#333',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        fontSize: '10px',
        cursor: 'pointer',
        fontWeight: '600'
    },
    exportBtn: {
        padding: '6px 14px',
        backgroundColor: '#333',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '11px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        letterSpacing: '0.3px'
    },
    exportModalContent: {
        maxWidth: '440px',
        display: 'flex',
        flexDirection: 'column'
    },
    exportModalBody: {
        padding: '16px 20px',
        overflowY: 'auto',
        minHeight: 0
    },
    exportModalFooter: {
        padding: '12px 20px',
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        flexShrink: 0
    },
    exportCheckbox: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '10px 12px',
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
        marginBottom: '8px',
        cursor: 'pointer',
        fontSize: '12px',
        transition: 'background-color 0.15s'
    },
    exportInvoiceBlock: {
        border: '1px solid #e5e5e0',
        borderRadius: '8px',
        marginBottom: '8px',
        backgroundColor: '#fafaf8'
    },
    invoicePeriodStatus: {
        margin: '0 12px 8px 34px',
        padding: '6px 8px',
        borderRadius: '6px',
        backgroundColor: '#f0f0ec',
        color: '#666',
        fontSize: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px'
    },
    invoicePeriodAction: {
        border: 'none',
        backgroundColor: 'transparent',
        color: '#333',
        padding: 0,
        fontSize: '10px',
        fontWeight: '700',
        textDecoration: 'underline',
        cursor: 'pointer'
    },
    exportInvoiceOptions: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '6px 12px',
        padding: '0 12px 10px 34px'
    },
    exportMiniCheckbox: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        minWidth: 0,
        color: '#555',
        cursor: 'pointer',
        fontSize: '10px'
    },
    exportInvoiceSeparator: {
        gridColumn: '1 / -1',
        height: '1px',
        backgroundColor: '#e8e8e3',
        margin: '2px 0'
    },
    exportWarning: {
        margin: '0 12px 7px 34px',
        color: '#9a5a00',
        fontSize: '10px'
    },
    exportError: {
        margin: '0 12px 9px 34px',
        color: '#c62828',
        fontSize: '10px'
    },
    progressBar: {
        width: '100%',
        height: '6px',
        backgroundColor: '#f0f0f0',
        borderRadius: '3px',
        overflow: 'hidden'
    },
    progressFill: {
        height: '100%',
        backgroundColor: '#333',
        borderRadius: '3px',
        transition: 'width 0.3s ease'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '12px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
    },
    selectedRow: {
        backgroundColor: '#e8f5e9'
    },
    verifiedRow: {
        backgroundColor: '#f2f8f2',
        boxShadow: 'inset 3px 0 0 #2e7d32'
    },
    th: {
        padding: 'var(--expenses-cell-padding)',
        fontSize: '10px',
        fontWeight: '600',
        textTransform: 'uppercase',
        color: '#888',
        borderBottom: '2px solid #eee'
    },
    td: {
        padding: 'var(--expenses-cell-padding)',
        fontSize: '12px',
        color: '#333'
    },
    receiptBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        padding: '2px'
    },
    receiptLabel: {
        cursor: 'pointer',
        fontSize: '14px',
        opacity: 0.3
    },
    summaryCard: {
        padding: '12px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        fontSize: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        marginBottom: '16px'
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '6px'
    },
    summaryTotal: {
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: '6px',
        borderTop: '1px solid #eee'
    },
    summaryMeta: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '6px',
        fontSize: '10px',
        color: '#999'
    },
    actionRow: {
        display: 'flex',
        gap: '6px',
        justifyContent: 'flex-end',
        marginBottom: '8px'
    },
    actionBtn: {
        padding: '5px 12px',
        backgroundColor: 'transparent',
        color: '#888',
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        fontSize: '10px',
        fontWeight: '500',
        cursor: 'pointer',
        letterSpacing: '0.3px',
        transition: 'all 0.15s'
    },
    creditGroupModeBtn: {
        padding: '5px 12px',
        backgroundColor: '#f4f7fb',
        color: '#315b82',
        border: '1px solid #c9d9eb',
        borderRadius: '6px',
        fontSize: '10px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    actionBtnPrimary: {
        padding: '5px 12px',
        backgroundColor: '#333',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '10px',
        fontWeight: '500',
        cursor: 'pointer',
        letterSpacing: '0.3px',
        transition: 'all 0.15s'
    },
    emptyState: {
        textAlign: 'center',
        padding: '20px 0',
        color: '#bbb',
        fontSize: '12px'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
    },
    modalContent: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '85vh',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
    },
    modalHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #eee',
        gap: '8px'
    },
    modalCloseBtn: {
        background: 'none',
        border: 'none',
        fontSize: '16px',
        cursor: 'pointer',
        color: '#999',
        padding: '4px'
    },
    modalBody: {
        padding: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f8f8f5',
        overflow: 'auto',
        maxHeight: '65vh'
    },
    modalFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px',
        borderTop: '1px solid #eee'
    },
    modalDeleteBtn: {
        padding: '6px 14px',
        backgroundColor: '#c62828',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '11px',
        cursor: 'pointer'
    }
};
